import React from 'react'
import { IMediaAccounts } from './PostScheduling'
import { Instagram, Facebook as FacebookIcon, Music, Calendar, User, Image, Video, AlertCircle, CheckCircle } from 'lucide-react'

interface ModernConfirmPostProps {
  imageFiles: any[]
  videoFiles: any[]
  captionValue: string
  pickerValue: any
  switchValue: boolean
  errorMessage?: string
  profileValue?: IMediaAccounts
  platform: string
  tiktokAdditionalData?: {
    privacy_level: string
    allow_comments: boolean
    allow_duet: boolean
    allow_stitch: boolean
    brand_content: boolean
    brand_organic: boolean
  }
}

export default function ModernConfirmPost({
  imageFiles,
  videoFiles,
  captionValue,
  pickerValue,
  switchValue,
  errorMessage,
  profileValue,
  platform,
  tiktokAdditionalData,
}: ModernConfirmPostProps): JSX.Element {
  const getPlatformIcon = () => {
    switch (platform) {
      case 'Instagram':
        return <Instagram size={24} style={{ color: '#e4405f' }} />
      case 'Facebook':
        return <FacebookIcon size={24} style={{ color: '#1877f2' }} />
      case 'TikTok':
        return <Music size={24} style={{ color: '#00f2ea' }} />
      default:
        return null
    }
  }

  const getPlatformColor = () => {
    switch (platform) {
      case 'Instagram':
        return 'linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)'
      case 'Facebook':
        return 'linear-gradient(135deg, #1877f2 0%, #0c5fcd 100%)'
      case 'TikTok':
        return 'linear-gradient(135deg, #00f2ea 0%, #ff0050 100%)'
      default:
        return 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)'
    }
  }

  const totalMedia = imageFiles.length + videoFiles.length
  const hasMedia = totalMedia > 0

  return (
    <div>
      <h2
        style={{
          fontSize: '32px',
          fontWeight: 700,
          background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '12px',
        }}
      >
        Review & Confirm
      </h2>
      <p
        style={{
          fontSize: '16px',
          color: 'rgba(255, 255, 255, 0.6)',
          marginBottom: '32px',
        }}
      >
        Take a final look at your post before publishing
      </p>

      {/* Error Message */}
      {errorMessage && (
        <div
          style={{
            padding: '16px 20px',
            borderRadius: '12px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <AlertCircle size={20} style={{ color: '#ef4444', flexShrink: 0 }} />
          <p
            style={{
              fontSize: '14px',
              color: '#ef4444',
              margin: 0,
            }}
          >
            {errorMessage}
          </p>
        </div>
      )}

      {/* Success Indicators */}
      {!errorMessage && (
        <div
          style={{
            padding: '16px 20px',
            borderRadius: '12px',
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <CheckCircle size={20} style={{ color: '#22c55e', flexShrink: 0 }} />
          <p
            style={{
              fontSize: '14px',
              color: '#22c55e',
              margin: 0,
            }}
          >
            Everything looks good! Your post is ready to {switchValue ? 'be scheduled' : 'publish now'}.
          </p>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Left Column - Post Preview */}
        <div
          style={{
            padding: '24px',
            borderRadius: '16px',
            background: 'rgba(30, 41, 59, 0.4)',
            border: '1px solid rgba(168, 85, 247, 0.2)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px',
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: getPlatformColor(),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {getPlatformIcon()}
            </div>
            <div>
              <h3
                style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: 'rgba(255, 255, 255, 0.9)',
                  margin: 0,
                }}
              >
                {profileValue?.accountName ?? 'Your Account'}
              </h3>
              <p
                style={{
                  fontSize: '13px',
                  color: 'rgba(255, 255, 255, 0.5)',
                  margin: 0,
                }}
              >
                {platform}
              </p>
            </div>
          </div>

          {/* Media Preview */}
          {hasMedia && (
            <div
              style={{
                marginBottom: '16px',
                borderRadius: '12px',
                overflow: 'hidden',
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid rgba(168, 85, 247, 0.15)',
              }}
            >
              {imageFiles.length > 0 && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns:
                      imageFiles.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                    gap: '4px',
                    maxHeight: '300px',
                  }}
                >
                  {imageFiles.slice(0, 4).map((file, index) => (
                    <div
                      key={index}
                      style={{
                        position: 'relative',
                        paddingBottom: imageFiles.length === 1 ? '100%' : '100%',
                        background: 'rgba(0, 0, 0, 0.3)',
                      }}
                    >
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Preview ${index + 1}`}
                        style={{
                          position: 'absolute',
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      {index === 3 && imageFiles.length > 4 && (
                        <div
                          style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'rgba(0, 0, 0, 0.7)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '24px',
                            fontWeight: 600,
                            color: '#fff',
                          }}
                        >
                          +{imageFiles.length - 4}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {videoFiles.length > 0 && imageFiles.length === 0 && (
                <div
                  style={{
                    position: 'relative',
                    paddingBottom: '100%',
                    background: 'rgba(0, 0, 0, 0.5)',
                  }}
                >
                  <video
                    src={URL.createObjectURL(videoFiles[0])}
                    style={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    controls
                  />
                </div>
              )}
            </div>
          )}

          {/* Caption */}
          <div
            style={{
              padding: '16px',
              borderRadius: '12px',
              background: 'rgba(15, 23, 42, 0.6)',
              border: '1px solid rgba(168, 85, 247, 0.15)',
            }}
          >
            <p
              style={{
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.8)',
                margin: 0,
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap',
              }}
            >
              {captionValue || (
                <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                  No caption added
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Right Column - Post Details */}
        <div>
          <div
            style={{
              padding: '24px',
              borderRadius: '16px',
              background: 'rgba(30, 41, 59, 0.4)',
              border: '1px solid rgba(168, 85, 247, 0.2)',
              marginBottom: '20px',
            }}
          >
            <h3
              style={{
                fontSize: '18px',
                fontWeight: 600,
                color: 'rgba(255, 255, 255, 0.9)',
                marginBottom: '20px',
              }}
            >
              Post Details
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Platform */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    background: 'rgba(168, 85, 247, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {getPlatformIcon()}
                </div>
                <div>
                  <p
                    style={{
                      fontSize: '12px',
                      color: 'rgba(255, 255, 255, 0.5)',
                      margin: 0,
                    }}
                  >
                    Platform
                  </p>
                  <p
                    style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'rgba(255, 255, 255, 0.9)',
                      margin: 0,
                    }}
                  >
                    {platform}
                  </p>
                </div>
              </div>

              {/* Account */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    background: 'rgba(168, 85, 247, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <User size={18} style={{ color: '#a855f7' }} />
                </div>
                <div>
                  <p
                    style={{
                      fontSize: '12px',
                      color: 'rgba(255, 255, 255, 0.5)',
                      margin: 0,
                    }}
                  >
                    Account
                  </p>
                  <p
                    style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'rgba(255, 255, 255, 0.9)',
                      margin: 0,
                    }}
                  >
                    {profileValue?.accountName ?? 'Not selected'}
                  </p>
                </div>
              </div>

              {/* Media Count */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    background: 'rgba(168, 85, 247, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {videoFiles.length > 0 ? (
                    <Video size={18} style={{ color: '#a855f7' }} />
                  ) : (
                    <Image size={18} style={{ color: '#a855f7' }} />
                  )}
                </div>
                <div>
                  <p
                    style={{
                      fontSize: '12px',
                      color: 'rgba(255, 255, 255, 0.5)',
                      margin: 0,
                    }}
                  >
                    Media
                  </p>
                  <p
                    style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'rgba(255, 255, 255, 0.9)',
                      margin: 0,
                    }}
                  >
                    {imageFiles.length > 0 && `${imageFiles.length} image${imageFiles.length > 1 ? 's' : ''}`}
                    {imageFiles.length > 0 && videoFiles.length > 0 && ' + '}
                    {videoFiles.length > 0 && `${videoFiles.length} video${videoFiles.length > 1 ? 's' : ''}`}
                    {!hasMedia && 'None'}
                  </p>
                </div>
              </div>

              {/* Schedule */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    background: 'rgba(168, 85, 247, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Calendar size={18} style={{ color: '#a855f7' }} />
                </div>
                <div>
                  <p
                    style={{
                      fontSize: '12px',
                      color: 'rgba(255, 255, 255, 0.5)',
                      margin: 0,
                    }}
                  >
                    Publishing
                  </p>
                  <p
                    style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'rgba(255, 255, 255, 0.9)',
                      margin: 0,
                    }}
                  >
                    {switchValue
                      ? new Date(pickerValue._d || pickerValue).toLocaleString()
                      : 'Immediately'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* TikTok Settings */}
          {platform === 'TikTok' && tiktokAdditionalData && (
            <div
              style={{
                padding: '20px',
                borderRadius: '16px',
                background: 'rgba(30, 41, 59, 0.4)',
                border: '1px solid rgba(168, 85, 247, 0.2)',
              }}
            >
              <h3
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: 'rgba(255, 255, 255, 0.9)',
                  marginBottom: '16px',
                }}
              >
                TikTok Settings
              </h3>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  fontSize: '13px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    Privacy Level:
                  </span>
                  <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                    {tiktokAdditionalData.privacy_level || 'Not set'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Comments:</span>
                  <span
                    style={{
                      color: tiktokAdditionalData.allow_comments ? '#22c55e' : '#ef4444',
                      fontWeight: 500,
                    }}
                  >
                    {tiktokAdditionalData.allow_comments ? 'Allowed' : 'Disabled'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Duet:</span>
                  <span
                    style={{
                      color: tiktokAdditionalData.allow_duet ? '#22c55e' : '#ef4444',
                      fontWeight: 500,
                    }}
                  >
                    {tiktokAdditionalData.allow_duet ? 'Allowed' : 'Disabled'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Stitch:</span>
                  <span
                    style={{
                      color: tiktokAdditionalData.allow_stitch ? '#22c55e' : '#ef4444',
                      fontWeight: 500,
                    }}
                  >
                    {tiktokAdditionalData.allow_stitch ? 'Allowed' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Caption Character Count */}
          <div
            style={{
              marginTop: '20px',
              padding: '16px 20px',
              borderRadius: '12px',
              background: 'rgba(168, 85, 247, 0.1)',
              border: '1px solid rgba(168, 85, 247, 0.2)',
            }}
          >
            <p
              style={{
                fontSize: '13px',
                color: 'rgba(255, 255, 255, 0.7)',
                margin: 0,
              }}
            >
              Caption length: <strong>{captionValue.length}</strong> characters
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}